(() => {
  if (customElements.get('nn-scratch-reveal')) return;

  class NnScratchReveal extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;

      this.initialized = true;
      this.canvas = this.querySelector('[data-scratch-canvas]');
      this.image = this.querySelector('[data-scratch-image]');
      this.coverImage = this.querySelector('.nn-scratch__cover-source');
      this.revealButton = this.querySelector('[data-scratch-reveal-all]');
      this.resetButton = this.querySelector('[data-scratch-reset]');
      this.status = this.querySelector('[data-scratch-status]');
      this.context = this.canvas?.getContext('2d', { willReadFrequently: true });
      this.isDrawing = false;
      this.checkCounter = 0;
      this.lastPoint = null;
      this.lastWidth = 0;
      this.lastHeight = 0;
      this.estimatedScratchedArea = 0;
      this.isSliderDragging = false;
      this.sliderDragMoved = false;

      if (!this.canvas || !this.context || !this.image) return;

      this.style.setProperty('--nn-scratch-cover-text', this.dataset.coverTextColor || '#fff7e8');
      this.image.setAttribute('aria-hidden', 'true');
      this.image.inert = true;
      this.setupSlider();

      this.canvas.addEventListener('pointerdown', this.onPointerDown.bind(this));
      this.canvas.addEventListener('pointermove', this.onPointerMove.bind(this));
      this.canvas.addEventListener('pointerup', this.onPointerUp.bind(this));
      this.canvas.addEventListener('pointercancel', this.onPointerUp.bind(this));
      this.canvas.addEventListener('lostpointercapture', this.onPointerUp.bind(this));
      this.revealButton?.addEventListener('click', () => this.reveal());
      this.resetButton?.addEventListener('click', () => this.reset());
      this.onCoverImageLoad = () => this.drawCover(true);
      this.coverImage?.addEventListener('load', this.onCoverImageLoad);

      this.resizeObserver = new ResizeObserver(() => this.drawCover());
      this.resizeObserver.observe(this.canvas);
      this.prepareCover();
    }

    disconnectedCallback() {
      this.resizeObserver?.disconnect();
      this.coverImage?.removeEventListener('load', this.onCoverImageLoad);
      if (this.scrollFrame) window.cancelAnimationFrame(this.scrollFrame);
    }

    async prepareCover() {
      if (this.coverImage && !this.coverImage.complete) {
        await new Promise((resolve) => {
          this.coverImage.addEventListener('load', resolve, { once: true });
          this.coverImage.addEventListener('error', resolve, { once: true });
        });
      }

      this.drawCover(true);
      this.classList.add('is-ready');
    }

    setupSlider() {
      this.sliderViewport = this.querySelector('[data-scratch-slider-viewport]');
      this.slides = Array.from(this.querySelectorAll('[data-scratch-slide]'));
      this.sliderDots = Array.from(this.querySelectorAll('[data-scratch-slider-dot]'));
      this.previousSlideButton = this.querySelector('[data-scratch-slider-prev]');
      this.nextSlideButton = this.querySelector('[data-scratch-slider-next]');
      this.currentSlide = 0;

      if (!this.sliderViewport || this.slides.length < 1) return;

      this.previousSlideButton?.addEventListener('click', () => this.goToSlide(this.currentSlide - 1, true));
      this.nextSlideButton?.addEventListener('click', () => this.goToSlide(this.currentSlide + 1, true));
      this.sliderDots.forEach((dot) => {
        dot.addEventListener('click', () => this.goToSlide(Number(dot.dataset.slideIndex || 0), true));
      });
      this.sliderViewport.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.goToSlide(this.currentSlide - 1, true);
        }
        if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.goToSlide(this.currentSlide + 1, true);
        }
      });
      this.sliderViewport.addEventListener('pointerdown', (event) => this.onSliderPointerDown(event));
      this.sliderViewport.addEventListener('pointermove', (event) => this.onSliderPointerMove(event));
      this.sliderViewport.addEventListener('pointerup', (event) => this.onSliderPointerUp(event));
      this.sliderViewport.addEventListener('pointercancel', (event) => this.onSliderPointerUp(event));
      this.sliderViewport.addEventListener('lostpointercapture', (event) => this.onSliderPointerUp(event));
      this.sliderViewport.addEventListener('dragstart', (event) => event.preventDefault());
      this.sliderViewport.addEventListener('scroll', () => {
        if (this.scrollFrame) window.cancelAnimationFrame(this.scrollFrame);
        this.scrollFrame = window.requestAnimationFrame(() => {
          const index = Math.round(this.sliderViewport.scrollLeft / Math.max(this.sliderViewport.clientWidth, 1));
          this.updateSlideState(index);
        });
      }, { passive: true });

      this.updateSlideState(0);
    }

    onSliderPointerDown(event) {
      if (
        event.pointerType === 'touch'
        || event.button !== 0
        || this.slides.length < 2
        || !this.classList.contains('is-revealed')
      ) return;

      this.isSliderDragging = true;
      this.sliderDragMoved = false;
      this.sliderDragStartX = event.clientX;
      this.sliderDragStartScrollLeft = this.sliderViewport.scrollLeft;
      this.sliderViewport.classList.add('is-dragging');
      this.sliderViewport.setPointerCapture(event.pointerId);
    }

    onSliderPointerMove(event) {
      if (!this.isSliderDragging) return;

      const distance = event.clientX - this.sliderDragStartX;
      if (Math.abs(distance) > 4) this.sliderDragMoved = true;
      if (this.sliderDragMoved) event.preventDefault();
      this.sliderViewport.scrollLeft = this.sliderDragStartScrollLeft - distance;
    }

    onSliderPointerUp(event) {
      if (!this.isSliderDragging) return;

      this.isSliderDragging = false;
      this.sliderViewport.classList.remove('is-dragging');
      if (this.sliderViewport.hasPointerCapture(event.pointerId)) {
        this.sliderViewport.releasePointerCapture(event.pointerId);
      }

      const index = Math.round(this.sliderViewport.scrollLeft / Math.max(this.sliderViewport.clientWidth, 1));
      this.goToSlide(index, this.sliderDragMoved);
    }

    goToSlide(index, announce = false) {
      if (!this.sliderViewport || this.slides.length < 2) return;

      const targetIndex = (index + this.slides.length) % this.slides.length;
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.sliderViewport.scrollTo({
        left: targetIndex * this.sliderViewport.clientWidth,
        behavior: reduceMotion ? 'auto' : 'smooth'
      });
      this.updateSlideState(targetIndex);

      if (announce && this.status) {
        this.status.textContent = `Bild ${targetIndex + 1} von ${this.slides.length}`;
      }
    }

    updateSlideState(index) {
      const safeIndex = Math.max(0, Math.min(index, this.slides.length - 1));
      this.currentSlide = safeIndex;
      this.slides.forEach((slide, slideIndex) => {
        slide.setAttribute('aria-hidden', slideIndex === safeIndex ? 'false' : 'true');
      });
      this.sliderDots.forEach((dot, dotIndex) => {
        if (dotIndex === safeIndex) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
    }

    drawCover(force = false) {
      const bounds = this.canvas.getBoundingClientRect();
      const width = Math.round(bounds.width);
      const height = Math.round(bounds.height);
      if (!width || !height) return;
      if (!force && width === this.lastWidth && height === this.lastHeight) return;

      this.lastWidth = width;
      this.lastHeight = height;
      this.estimatedScratchedArea = 0;
      const pixelRatio = Math.min(window.devicePixelRatio || 1, 2);
      this.canvas.width = Math.round(width * pixelRatio);
      this.canvas.height = Math.round(height * pixelRatio);
      this.context.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
      this.context.globalCompositeOperation = 'source-over';

      const startColor = this.dataset.coverColor || '#4f2c1d';
      const endColor = this.dataset.coverAccent || startColor;
      const gradient = this.context.createLinearGradient(0, 0, width, height);
      gradient.addColorStop(0, startColor);
      gradient.addColorStop(1, endColor);
      this.context.fillStyle = gradient;
      this.context.fillRect(0, 0, width, height);

      if (this.coverImage?.complete && this.coverImage.naturalWidth) {
        this.drawContainedImage(this.coverImage, width, height);
      }

      const tintOpacity = Math.max(0, Math.min(50, Number(this.dataset.coverTintOpacity || 20))) / 100;
      if (tintOpacity > 0) {
        this.context.fillStyle = `rgba(53, 23, 15, ${tintOpacity})`;
        this.context.fillRect(0, 0, width, height);
      }

      this.canvas.style.background = 'transparent';
      if (!this.classList.contains('is-revealed')) this.classList.remove('has-started');
    }

    drawContainedImage(image, canvasWidth, canvasHeight) {
      const scale = Math.min(canvasWidth / image.naturalWidth, canvasHeight / image.naturalHeight);
      const width = image.naturalWidth * scale;
      const height = image.naturalHeight * scale;
      const x = (canvasWidth - width) / 2;
      const y = (canvasHeight - height) / 2;
      this.context.drawImage(image, x, y, width, height);
    }

    pointFromEvent(event) {
      const bounds = this.canvas.getBoundingClientRect();
      return {
        x: event.clientX - bounds.left,
        y: event.clientY - bounds.top
      };
    }

    onPointerDown(event) {
      if (this.classList.contains('is-revealed')) return;

      event.preventDefault();
      this.isDrawing = true;
      this.classList.add('has-started');
      this.canvas.setPointerCapture(event.pointerId);
      this.lastPoint = this.pointFromEvent(event);
      this.erase(this.lastPoint, this.lastPoint);
    }

    onPointerMove(event) {
      if (!this.isDrawing || this.classList.contains('is-revealed')) return;

      event.preventDefault();
      const point = this.pointFromEvent(event);
      this.erase(this.lastPoint, point);
      this.lastPoint = point;
      this.checkCounter += 1;

      if (this.checkCounter % 6 === 0) this.checkProgress();
    }

    onPointerUp(event) {
      if (!this.isDrawing) return;

      this.isDrawing = false;
      this.lastPoint = null;
      if (this.canvas.hasPointerCapture(event.pointerId)) {
        this.canvas.releasePointerCapture(event.pointerId);
      }
      this.checkProgress();
    }

    erase(from, to) {
      const bounds = this.canvas.getBoundingClientRect();
      const brushSize = Math.max(32, Math.min(64, Math.min(bounds.width, bounds.height) * 0.1));
      const distance = Math.hypot(to.x - from.x, to.y - from.y);
      this.estimatedScratchedArea += Math.max(distance, brushSize * 0.35) * brushSize;

      this.context.save();
      this.context.globalCompositeOperation = 'destination-out';
      this.context.lineCap = 'round';
      this.context.lineJoin = 'round';
      this.context.lineWidth = brushSize;
      this.context.beginPath();
      this.context.moveTo(from.x, from.y);
      this.context.lineTo(to.x, to.y);
      this.context.stroke();
      this.context.restore();
    }

    scratchedPercentage() {
      try {
        const pixels = this.context.getImageData(0, 0, this.canvas.width, this.canvas.height).data;
        const pixelRatio = Math.min(window.devicePixelRatio || 1, 2);
        const spacing = Math.max(16, Math.round(22 * pixelRatio));
        let transparent = 0;
        let samples = 0;

        for (let y = Math.floor(spacing / 2); y < this.canvas.height; y += spacing) {
          for (let x = Math.floor(spacing / 2); x < this.canvas.width; x += spacing) {
            samples += 1;
            if (pixels[(y * this.canvas.width + x) * 4 + 3] < 40) transparent += 1;
          }
        }

        return samples ? transparent / samples * 100 : 0;
      } catch (error) {
        const area = Math.max(this.lastWidth * this.lastHeight, 1);
        return Math.min(this.estimatedScratchedArea / area * 75, 100);
      }
    }

    checkProgress() {
      if (this.scratchedPercentage() >= Number(this.dataset.revealThreshold || 50)) this.reveal();
    }

    reveal() {
      if (this.classList.contains('is-revealed')) return;

      this.classList.add('is-revealed');
      this.canvas.setAttribute('aria-hidden', 'true');
      this.image.removeAttribute('aria-hidden');
      this.image.inert = false;
      this.revealButton?.setAttribute('hidden', '');
      this.resetButton?.removeAttribute('hidden');
      if (this.status) this.status.textContent = this.dataset.revealedStatus || '';
    }

    reset() {
      this.classList.remove('is-revealed');
      this.image.setAttribute('aria-hidden', 'true');
      this.image.inert = true;
      this.canvas.setAttribute('aria-hidden', 'true');
      this.revealButton?.removeAttribute('hidden');
      this.resetButton?.setAttribute('hidden', '');
      if (this.status) this.status.textContent = this.dataset.resetStatus || '';
      this.goToSlide(0);
      this.drawCover(true);
      this.revealButton?.focus();
    }
  }

  customElements.define('nn-scratch-reveal', NnScratchReveal);
})();
